import { useMemo, useState } from "react";
import {
  assetUrl,
  localized,
  pageUrl,
  RichText,
  stripHtml,
} from "../utils.jsx";
import { Icon } from "../Icon.jsx";

export function HeroCarousel({ config }) {
  const slides = config.homepage?.media_content || [];
  const [active, setActive] = useState(0);
  const slide = slides[active] || null;
  const fallbackTitle =
    config.lang === "ID"
      ? "KEAMANAN DIMULAI DARI ANDA"
      : "SECURITY STARTS WITH YOU";
  const next = (direction) => {
    if (slides.length < 2) return;
    setActive(
      (current) => (current + direction + slides.length) % slides.length,
    );
  };
  return (
    <section className="sq-react-hero" aria-label="Featured SQURA content">
      <picture className="sq-react-hero__media">
        {slide?.media_path_mobile && (
          <source
            media="(max-width: 767px)"
            srcSet={assetUrl(slide.media_path_mobile)}
          />
        )}
        <img
          src={assetUrl(
            slide?.media_path || "templates/fe/images/banner-home.png",
          )}
          alt=""
          fetchPriority="high"
        />
      </picture>
      <div className="sq-react-hero__shade" />
      <div className="sq-react-container sq-react-hero__content">
        <span className="sq-react-hero__eyebrow">SQURA Cyber Security</span>
        {slide?.media_title ? (
          <RichText
            as="h1"
            className="sq-react-hero__title"
            html={slide.media_title}
          />
        ) : (
          <h1 className="sq-react-hero__title">{fallbackTitle}</h1>
        )}
        <a
          className="sq-hero-button"
          href={
            slide?.media_link ||
            pageUrl(localized(config.menus[1], "menu_link", config.lang))
          }
        >
          <span>{config.labels["btn-banner"]}</span>
          <Icon name="arrow_forward" />
        </a>
        <img
          className="sq-react-hero__partner"
          src={assetUrl("templates/fe/images/lintas-arta.png")}
          alt="Lintasarta"
        />
      </div>
      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="sq-hero-arrow is-prev"
            onClick={() => next(-1)}
            aria-label="Slide sebelumnya"
          >
            <Icon name="arrow_back_ios_new" />
          </button>
          <button
            type="button"
            className="sq-hero-arrow is-next"
            onClick={() => next(1)}
            aria-label="Slide berikutnya"
          >
            <Icon name="arrow_forward_ios" />
          </button>
          <div className="sq-hero-dots" aria-label="Pilih slide">
            {slides.map((item, index) => (
              <button
                key={item.id || index}
                type="button"
                onClick={() => setActive(index)}
                className={active === index ? "is-active" : ""}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function PartnersAndIntro({ config }) {
  const intro = config.content?.[0];
  return (
    <section className="sq-react-intro">
      <div className="sq-react-container">
        <div className="sq-partners">
          <p>
            {config.lang === "EN" ? "Our Global Partner" : "Mitra Global Kami"}
          </p>
          <div>
            {config.partners.map((partner) => (
              <img
                key={partner.id || partner.img_url}
                src={assetUrl(partner.img_url)}
                alt={partner.img_name || "SQURA global partner"}
                loading="lazy"
              />
            ))}
          </div>
        </div>
        {intro && (
          <div className="sq-cyber-intro">
            <div>
              <span className="sq-section-kicker">{intro.long_title}</span>
              <RichText as="h2" html={intro.banner_title} />
              <RichText className="sq-rich-copy" html={intro.banner_desc} />
              <a
                className="sq-primary-button"
                href={pageUrl(intro.url_internal)}
                target="_blank"
                rel="noreferrer"
              >
                {config.labels["btn-learnmore"]}
              </a>
            </div>
            <img
              src={assetUrl(intro.image_banner)}
              alt={stripHtml(intro.banner_title)}
              loading="lazy"
            />
          </div>
        )}
      </div>
    </section>
  );
}

export function SolutionsShowcase({ config }) {
  const groups =
    config.menus.find(
      (item) => item.menu_code === "1ecd6fa4-1437-4caa-9151-24caea885753",
    )?.children || [];
  const [active, setActive] = useState(0);
  const selected = groups[active] || groups[0];
  if (!selected) return null;
  return (
    <section className="sq-solutions">
      <div className="sq-solutions__nav">
        <span className="sq-section-kicker">
          {config.labels["title-solution-left"]}
        </span>
        <h2>
          {config.lang === "EN"
            ? "Start with your security challenge"
            : "Mulai dari tantangan keamanan Anda"}
        </h2>
        <div role="tablist" aria-label="Security challenges">
          {groups.map((group, index) => (
            <button
              key={group.id}
              role="tab"
              aria-selected={active === index}
              className={active === index ? "is-active" : ""}
              onClick={() => setActive(index)}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              {localized(group, "title", config.lang) ||
                localized(group, "menu_value", config.lang)}
              <Icon name="arrow_forward" />
            </button>
          ))}
        </div>
      </div>
      <div className="sq-solutions__content">
        <span className="sq-section-kicker">
          {config.labels["title-solution-right"]}
        </span>
        <div className="sq-solution-grid">
          {(selected.children || []).map((solution) => (
            <article key={solution.id}>
              <div className="sq-solution-card__icon">
                <Icon name="security" />
              </div>
              <h3>{localized(solution, "menu_value", config.lang)}</h3>
              <p>{stripHtml(localized(solution, "short_desc", config.lang))}</p>
              <a href={pageUrl(localized(solution, "menu_link", config.lang))}>
                {config.labels["btn-learnmore"]}
                <Icon name="arrow_forward" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IndustriesSection({ config }) {
  const industries =
    config.menus.find(
      (item) => item.menu_code === "1030bc62-3179-4111-a669-429020e875b0",
    )?.children || [];
  return (
    <section className="sq-industries-section">
      <div className="sq-react-container">
        <span className="sq-section-kicker">Industries</span>
        <h2>{config.labels["title-cybersecurity"]}</h2>
        <div className="sq-industry-grid">
          {industries.map((industry) => (
            <article key={industry.id}>
              <div className="sq-industry-card__image">
                <img
                  src={assetUrl(industry.img_thumbnail)}
                  alt=""
                  loading="lazy"
                />
                <h3>{localized(industry, "menu_value", config.lang)}</h3>
              </div>
              <div className="sq-industry-card__body">
                <p>
                  {stripHtml(
                    localized(industry, "short_desc", config.lang),
                  ).slice(0, 190)}
                </p>
                <a
                  href={pageUrl(localized(industry, "menu_link", config.lang))}
                >
                  {config.labels["btn-learnmore"]}
                  <Icon name="arrow_forward" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ContactCta({ config }) {
  return (
    <section className="sq-contact-cta">
      <div className="sq-react-container">
        <div className="sq-contact-cta__copy">
          <span className="sq-section-kicker">
            {config.labels["title-lets-talk"]}
          </span>
          <h2>
            {config.labels["header-1-lets-talk"]}
            <br />
            {config.labels["header-2-lets-talk"]}
          </h2>
          <p>
            {config.lang === "EN"
              ? "Get in touch with our support team for customized security solutions or inquiries."
              : "Konsultasi untuk mendapatkan solusi yang disesuaikan dengan kebutuhan Anda."}
          </p>
          <a className="sq-secondary-button" href={config.urls.consult}>
            {config.labels["btn-lets-talk"]}
          </a>
        </div>
        <img
          src={assetUrl("templates/fe/images/asian-girl.png")}
          alt="SQURA cyber security consultant"
          loading="lazy"
        />
      </div>
    </section>
  );
}

export function VirtualTourSection({ config, onStart }) {
  const tours = useMemo(
    () => (config.content || []).slice(2),
    [config.content],
  );
  return tours.map((tour) => (
    <section
      className="sq-virtual-tour"
      key={tour.id}
      style={{
        "--sq-tour-desktop": `url("${assetUrl("templates/fe/images/asian-guy-monitor.png")}")`,
        "--sq-tour-mobile": `url("${assetUrl("templates/fe/images/asian-guy-monitor-mobile.png")}")`,
      }}
    >
      <div className="sq-react-container">
        <span className="sq-section-kicker">{tour.long_title}</span>
        <RichText as="h2" html={tour.banner_title} />
        <RichText className="sq-rich-copy" html={tour.banner_desc} />
        <button type="button" className="sq-secondary-button" onClick={onStart}>
          {config.labels["start-tour"]}
        </button>
      </div>
    </section>
  ));
}
